import { Badge, Box, Button, Card, Group, Stack, Text } from '@mantine/core'
import { IconArrowLeft } from '@tabler/icons-react'
import React, { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import {
	getSpeakingSession,
	getSpeakingSessions,
	SpeakingReviewMistake,
	SpeakingSessionDetail,
	SpeakingSessionRow,
	SpeakingTranscriptTurn,
	unwrapList,
	unwrapObject,
} from 'src/apis/courseAiAPI'
import CustomDrawer from 'src/components/CustomComponents/CustomDrawer'
import { formatDateTime } from './learnerDisplay'
import {
	categoryLabel,
	correctionText,
	criteriaText,
	CRITERIA_LABELS,
	endReasonLabel,
	formatDurationMs,
	formatUsd,
	modeLabel,
	scoreText,
	turnKindLabel,
} from './speakingDisplay'

/** The learner a drawer is opened for: a row of Manage Learners in a Speaking course. */
export type SpeakingSessionsLearner = {
	user_id: string
	name?: string | null
	course_id: string
	course_name?: string | null
}

const PAGE_LIMIT = 50

const errorText = (error: any, fallback: string) =>
	error?.response?.data?.message ||
	error?.response?.data?.results?.message ||
	fallback

const STATUS_COLORS: Record<string, string> = {
	submitted: 'green',
	active: 'blue',
	ending: 'yellow',
}

function Audio({ label, url }: { label: string; url?: string | null }) {
	if (!url) return null
	return (
		<Stack spacing={2}>
			<Text size="xs" color="dimmed">
				{label}
			</Text>
			{/* preload none: a talk has dozens of clips and the staff plays the one in dispute */}
			<audio
				controls
				preload="none"
				src={url}
				style={{ width: '100%', height: 34 }}
				aria-label={label}
			/>
		</Stack>
	)
}

function CriteriaBadges({
	criteria,
}: {
	criteria: SpeakingSessionRow['criteria']
}) {
	if (!criteria)
		return (
			<Text size="xs" color="dimmed">
				No criteria yet
			</Text>
		)
	return (
		<Group spacing={4}>
			{CRITERIA_LABELS.filter(
				([key]) => criteria[key] !== null && criteria[key] !== undefined,
			).map(([key, label]) => (
				<Badge key={key} size="sm" variant="light" color="gray" title={label}>
					{label.slice(0, 1)} {criteria[key]}
				</Badge>
			))}
		</Group>
	)
}

function SessionRowCard({
	row,
	onOpen,
}: {
	row: SpeakingSessionRow
	onOpen: (id: string) => void
}) {
	return (
		<Card
			withBorder
			radius="md"
			padding="sm"
			data-testid="speaking-session-row"
			sx={{ cursor: 'pointer' }}
			onClick={() => onOpen(row.id)}
			role="button"
			tabIndex={0}
			aria-label={`Open the talk of ${row.topic.date || formatDateTime(row.started_at)}`}
			onKeyDown={(event: React.KeyboardEvent) => {
				if (event.key === 'Enter' || event.key === ' ') onOpen(row.id)
			}}
		>
			<Group position="apart" align="flex-start" noWrap>
				<Stack spacing={3} sx={{ minWidth: 0 }}>
					<Group spacing={6}>
						<Text size="sm" fw={700}>
							{row.topic.date || formatDateTime(row.started_at)}
						</Text>
						<Badge
							size="sm"
							variant="light"
							color={row.mode === 'ielts' ? 'violet' : 'green'}
						>
							{modeLabel(row.mode)}
						</Badge>
						<Badge
							size="sm"
							variant="light"
							color={STATUS_COLORS[row.status] || 'gray'}
						>
							{row.status}
						</Badge>
					</Group>
					<Text size="sm" sx={{ lineHeight: 1.3 }}>
						{row.topic.title || '-'}
						{row.topic.category ? (
							<Text component="span" size="xs" color="dimmed">
								{' '}
								({categoryLabel(row.topic.category)})
							</Text>
						) : null}
					</Text>
					<CriteriaBadges criteria={row.criteria} />
					<Text size="xs" color="dimmed">
						{endReasonLabel(row.end_reason, row.status)}, cost{' '}
						{formatUsd(row.cost_usd)}
					</Text>
				</Stack>
				<Stack spacing={0} align="center">
					<Text
						size="xl"
						fw={800}
						sx={{ lineHeight: 1 }}
						data-testid="speaking-session-score"
					>
						{scoreText(row.score)}
					</Text>
					<Text size="xs" color="dimmed">
						score
					</Text>
				</Stack>
			</Group>
		</Card>
	)
}

function TurnBlock({ turn }: { turn: SpeakingTranscriptTurn }) {
	const hasLearner = Boolean(turn.learner.transcript || turn.learner.audio_url)
	const hasAi = Boolean(turn.ai.reply_text || turn.ai.audio_url)
	return (
		<Stack spacing={6} data-testid="speaking-turn">
			<Text size="xs" color="dimmed">
				#{turn.seq} {turnKindLabel(turn.kind)}
				{turn.question_index !== null && turn.question_index !== undefined
					? `, question ${turn.question_index + 1}`
					: ''}
				{turn.part ? `, ${turn.part}` : ''}
				{turn.cost_usd ? `, ${formatUsd(turn.cost_usd)}` : ''}
			</Text>
			{hasLearner ? (
				<Box
					sx={{
						background: '#f7faf7',
						border: '1px solid #e6ebe7',
						borderRadius: 10,
						padding: '8px 10px',
					}}
				>
					<Text size="xs" fw={700} color="dimmed">
						Learner
						{turn.learner.language && turn.learner.language !== 'en'
							? ` (${turn.learner.language})`
							: ''}
						{turn.learner.duration_ms
							? `, ${formatDurationMs(turn.learner.duration_ms)}`
							: ''}
					</Text>
					{turn.learner.transcript ? (
						<Text size="sm" sx={{ whiteSpace: 'pre-wrap' }}>
							{turn.learner.transcript}
						</Text>
					) : null}
					<Audio label="Learner audio" url={turn.learner.audio_url} />
					{turn.corrections.length > 0 ? (
						<Stack spacing={1} mt={4} data-testid="speaking-corrections">
							<Text size="xs" fw={700} color="orange">
								Corrections
							</Text>
							{turn.corrections.map((correction, index) => (
								<Text key={index} size="xs">
									{correctionText(correction)}
								</Text>
							))}
						</Stack>
					) : null}
					{turn.pronunciation.score !== null &&
					turn.pronunciation.score !== undefined ? (
						<Text size="xs" color="dimmed" mt={2}>
							Pronunciation {turn.pronunciation.score}
						</Text>
					) : null}
				</Box>
			) : null}
			{hasAi ? (
				<Box
					sx={{
						background: '#ecfdf3',
						border: '1px solid #cfe4d6',
						borderRadius: 10,
						padding: '8px 10px',
					}}
				>
					<Text size="xs" fw={700} color="dimmed">
						AI tutor
						{turn.ai.duration_ms
							? `, ${formatDurationMs(turn.ai.duration_ms)}`
							: ''}
					</Text>
					{turn.ai.reply_text ? (
						<Text size="sm">{turn.ai.reply_text}</Text>
					) : null}
					<Audio label="AI audio" url={turn.ai.audio_url} />
				</Box>
			) : null}
		</Stack>
	)
}

function MistakeLine({ mistake }: { mistake: SpeakingReviewMistake }) {
	return (
		<Stack spacing={2}>
			<Text size="sm">
				<Badge size="xs" variant="light" color="red" mr={6}>
					{mistake.error_type}
				</Badge>
				{mistake.error || mistake.word || '-'}
				{mistake.correction ? ` -> ${mistake.correction}` : ''}
			</Text>
			{mistake.explanation ? (
				<Text size="xs" color="dimmed">
					{mistake.explanation}
				</Text>
			) : null}
			<Audio label="Mistake clip" url={mistake.audio_url} />
		</Stack>
	)
}

function SessionDetail({ detail }: { detail: SpeakingSessionDetail }) {
	const { session, review, transcript } = detail
	const ielts = review.ielts
	return (
		<Stack spacing="md" data-testid="speaking-session-detail">
			<Card withBorder radius="md" padding="sm">
				<Group position="apart" align="flex-start" noWrap>
					<Stack spacing={3}>
						<Text size="sm" fw={700}>
							{review.topic?.title || '-'}
						</Text>
						<Group spacing={6}>
							<Badge
								size="sm"
								variant="light"
								color={session.mode === 'ielts' ? 'violet' : 'green'}
							>
								{modeLabel(session.mode)}
							</Badge>
							<Badge
								size="sm"
								variant="light"
								color={STATUS_COLORS[session.status] || 'gray'}
							>
								{session.status}
							</Badge>
							<Badge size="sm" variant="light" color="gray">
								{session.character_id}, {session.level}
							</Badge>
						</Group>
						<Text size="xs" color="dimmed">
							{formatDateTime(session.started_at)}
							{session.ended_at
								? ` to ${formatDateTime(session.ended_at)}`
								: ''}
							, limit {Math.round(session.limit_seconds / 60)} min
						</Text>
						<Text size="xs" color="dimmed">
							{endReasonLabel(session.end_reason, session.status)}. Learner
							spoke {formatDurationMs(session.learner_audio_ms)}, Vietnamese
							turns {session.vietnamese_turns}, silence turns{' '}
							{session.silence_turns}.
						</Text>
						<Text size="xs" color="dimmed">
							Cost {formatUsd(session.cost_usd)}
							{session.client_version ? `, app ${session.client_version}` : ''}
						</Text>
					</Stack>
					<Stack spacing={0} align="center">
						<Text size="xl" fw={800} sx={{ lineHeight: 1 }}>
							{scoreText(review.score)}
						</Text>
						<Text size="xs" color="dimmed">
							score
						</Text>
					</Stack>
				</Group>
				<Text size="xs" mt={6} data-testid="speaking-criteria">
					{criteriaText(review.criteria)}
				</Text>
				{ielts ? (
					<Text size="xs" mt={2} data-testid="speaking-ielts">
						IELTS band {ielts.overall_band ?? '-'}
					</Text>
				) : null}
			</Card>

			<Stack spacing="md">
				<Text size="xs" fw={700} color="dimmed" tt="uppercase">
					Transcript ({transcript.length} turns)
				</Text>
				{transcript.length === 0 ? (
					<Text size="sm" color="dimmed">
						No turns were recorded.
					</Text>
				) : null}
				{transcript.map((turn) => (
					<TurnBlock key={turn.id} turn={turn} />
				))}
			</Stack>

			{review.questions.some(
				(question) => question.suggestion || question.mistakes.length > 0,
			) ? (
				<Stack spacing="sm">
					<Text size="xs" fw={700} color="dimmed" tt="uppercase">
						Review by question
					</Text>
					{review.questions.map((question) =>
						question.suggestion || question.mistakes.length > 0 ? (
							<Card key={question.index} withBorder radius="md" padding="sm">
								<Text size="sm" fw={600}>
									{question.index + 1}. {question.question || '-'}
								</Text>
								{question.suggestion ? (
									<Text size="sm" color="dimmed" mt={2}>
										Suggestion: {question.suggestion}
									</Text>
								) : null}
								<Audio
									label="Suggestion audio"
									url={question.suggestion_audio_url}
								/>
								<Stack spacing={6} mt={6}>
									{question.mistakes.map((mistake) => (
										<MistakeLine key={mistake.id} mistake={mistake} />
									))}
								</Stack>
							</Card>
						) : null,
					)}
				</Stack>
			) : null}
			{review.other_mistakes.length > 0 ? (
				<Stack spacing={6}>
					<Text size="xs" fw={700} color="dimmed" tt="uppercase">
						Other mistakes
					</Text>
					{review.other_mistakes.map((mistake) => (
						<MistakeLine key={mistake.id} mistake={mistake} />
					))}
				</Stack>
			) : null}
		</Stack>
	)
}

/**
 * The Speaking talks of one learner (UD-671, design G): a list with the day, topic, mode, score, criteria, why
 * it ended and what it cost, and a talk opened as its transcript with the learner's and the AI's audio. For
 * support disputes. SUPERADMIN only: the server holds the same rule, this page already does.
 */
function SpeakingSessionsDrawer({
	learner,
	onClose,
}: {
	learner: SpeakingSessionsLearner | null
	onClose: () => void
}) {
	const [rows, setRows] = useState<SpeakingSessionRow[]>([])
	const [loadError, setLoadError] = useState<string | null>(null)
	const [count, setCount] = useState(0)
	const [loading, setLoading] = useState(false)
	const [detail, setDetail] = useState<SpeakingSessionDetail | null>(null)
	const [openId, setOpenId] = useState<string | null>(null)
	const userId = learner?.user_id || null
	const courseId = learner?.course_id || null

	useEffect(() => {
		setRows([])
		setCount(0)
		setDetail(null)
		setOpenId(null)
		setLoadError(null)
		if (!userId || !courseId) return undefined
		let active = true
		setLoading(true)
		getSpeakingSessions({
			user_id: userId,
			course_id: courseId,
			limit: PAGE_LIMIT,
			offset: 0,
		})
			.then((response) => {
				if (!active) return
				const list = unwrapList(response)
				setRows(list.rows as SpeakingSessionRow[])
				setCount(list.count)
			})
			.catch((error) => {
				if (!active) return
				// UD-704: a failed load is said as such, never shown as an empty list.
				const message = errorText(error, 'Could not load the Speaking sessions')
				setLoadError(message)
				toast.error(message)
			})
			.finally(() => {
				if (active) setLoading(false)
			})
		return () => {
			active = false
		}
	}, [userId, courseId])

	const open = useCallback(async (id: string) => {
		setOpenId(id)
		setDetail(null)
		setLoading(true)
		try {
			const response = await getSpeakingSession(id)
			setDetail(unwrapObject<SpeakingSessionDetail>(response))
		} catch (error) {
			toast.error(errorText(error, 'Could not load this talk'))
			setOpenId(null)
		} finally {
			setLoading(false)
		}
	}, [])

	const back = () => {
		setOpenId(null)
		setDetail(null)
	}

	return (
		<CustomDrawer
			opened={Boolean(learner)}
			onClose={onClose}
			loading={loading}
			width={560}
			title={
				<>
					<span style={{ display: 'block' }}>Speaking sessions</span>
					<span
						style={{
							display: 'block',
							fontSize: 14,
							fontWeight: 400,
							color: '#868e96',
						}}
					>
						{learner?.name || '-'}
						{learner?.course_name ? `, ${learner.course_name}` : ''}
					</span>
				</>
			}
		>
			<Stack spacing="sm" sx={{ padding: '14px 18px' }}>
				{openId ? (
					<>
						<Group>
							<Button
								variant="subtle"
								size="xs"
								compact
								leftIcon={<IconArrowLeft size={14} />}
								onClick={back}
							>
								All sessions
							</Button>
						</Group>
						{detail ? <SessionDetail detail={detail} /> : null}
					</>
				) : (
					<>
						{!loading && loadError ? (
							<Text size="sm" color="red">
								Could not load the Speaking sessions: {loadError}
							</Text>
						) : null}
						{!loading && !loadError && rows.length === 0 ? (
							<Text size="sm" color="dimmed">
								This learner has no Speaking sessions yet.
							</Text>
						) : null}
						{rows.length > 0 ? (
							<Text size="xs" color="dimmed">
								{count} session{count === 1 ? '' : 's'}, newest first
								{count > rows.length ? `, the latest ${rows.length} shown` : ''}
							</Text>
						) : null}
						{rows.map((row) => (
							<SessionRowCard key={row.id} row={row} onOpen={open} />
						))}
					</>
				)}
			</Stack>
		</CustomDrawer>
	)
}

export default SpeakingSessionsDrawer
